import {
  AssetCondition,
  AssetStatus,
  CurrencyCodeEnum,
  EmployeeStatusEnum,
  SortOrder,
} from '@electricjs/core_entity-client';
import CenteredSpinner from 'common/CenteredSpinner';
import Papa from 'papaparse';
import { useMemo, useState } from 'react';
import * as yup from 'yup';

import {
  Banner,
  Box,
  Button,
  ColorsV2,
  FilePicker,
  Flex,
  Icon,
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalV2,
  spaces,
  sizes,
  Text,
  useThemeColors,
} from '@electricjs/arc';

import AssetError from '@/components/Assets/AssetDetails/AssetError';
import { EnumValuesList } from '@/components/Assets/EnumValuesList';
import {
  transformProductTypesToOptions,
  transformSitesToOptions,
  getAssetConditionDisplayNameMap,
  SYSTEM_MANAGED_ASSET_STATUSES,
  type ProductTypeOption,
  type AssetStatusOption,
  type AssetConditionOption,
} from '@/components/Assets/helpers/transformOptions';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { InlineAccordion } from '@/components/InlineAccordion';
import { transformEmployeesToOptions } from '@/components/People/transformOptions';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useOrganizationProductTypes } from '@/hooks/useOrganizationProductTypes';
import { assetStatusTranslationKeys } from '@/pages/Assets/AssetStatus';
import { useTranslation } from 'react-i18next';
import {
  useCreateAssetsMutation,
  useGetOrganizationSitesQuery,
  useListAssetsV2Query,
} from '@/redux/slices/assetApiSlice';
import { useGetOrganizationEmployeesQuery } from '@/redux/slices/organizationApiSlice';
import { CSVImportAsset } from '@/types/assets';

import { getAssetSchema } from './AssetSchema';
import { exportCSV } from 'common/utils/exportCsv';
import { findOptionCaseInsensitive } from 'common/utils/csvHelpers';
import {
  assetCSVColumnHeadersMap,
  assetCSVLanguage,
  assetCSVLanguageFormat,
  generateImportTemplateCSVData,
} from './helpers/assetsCSVLanguage';
import {
  CSVLanguage,
  CSVLanguages,
} from '../People/helpers/employeeCSVLanguages';
import { useIsAdpWfnCanadaConnectedOrg } from '@/hooks/useIsAdpWfnCanadaConnectedOrg';

enum FileStatus {
  Empty = 'empty',
  InvalidContent = 'invalidContent',
  InvalidHeader = 'invalidHeader',
  Undefined = 'undefined',
  Valid = 'valid',
}

const findCurrencyCodeCaseInsensitive = (
  value: string | undefined
): CurrencyCodeEnum | undefined => {
  if (!value?.trim()) return undefined;
  const normalizedValue = value.trim().toLowerCase();
  return Object.values(CurrencyCodeEnum).find(
    code => code.toLowerCase() === normalizedValue
  );
};

type AssetsCSVUploadModalProps = {
  onCancel: () => void;
  onSuccess: () => void;
};

const AssetsCSVUploadModal = ({
  onCancel,
  onSuccess,
}: AssetsCSVUploadModalProps) => {
  const { t, i18n } = useTranslation('assets');
  const isAdpWfnCanada = useIsAdpWfnCanadaConnectedOrg();
  const organizationId = useGetOrganizationId();
  const { space_xs } = spaces;
  const [enumPanelBackgroundColor, enumPanelBorderColor] = useThemeColors([
    ColorsV2.BACKGROUND_ALT,
    ColorsV2.BORDER,
  ]);

  const [createAssets, { isLoading: isCreateAssetsLoading }] =
    useCreateAssetsMutation();

  const {
    data: sites,
    isFetching: isFetchingSites,
    isError: isSitesError,
  } = useGetOrganizationSitesQuery({ organizationId });
  const siteOptions = useMemo(
    () =>
      transformSitesToOptions(
        (sites || []).filter(site => !!site.organizationId)
      ),
    [sites]
  );

  const {
    data: employees,
    isFetching: isFetchingEmployees,
    isError: isEmployeesError,
  } = useGetOrganizationEmployeesQuery({ organizationId });
  const employeeOptions = useMemo(
    () =>
      transformEmployeesToOptions(
        employees?.filter(
          employee => employee.status === EmployeeStatusEnum.Active
        ) ?? [],
        i18n.language,
        isAdpWfnCanada
      ),
    [employees, i18n.language, isAdpWfnCanada]
  );

  const {
    productTypes,
    isFetching: isFetchingProductTypes,
    isError: isProductTypesError,
  } = useOrganizationProductTypes();

  const productTypeOptions = useMemo(
    () => transformProductTypesToOptions(productTypes || []),
    [productTypes]
  );

  const {
    data: existingAssetsData,
    isFetching: isFetchingExistingAssets,
    isError: isExistingAssetsError,
  } = useListAssetsV2Query({
    organizationId,
    orderBy: 'name',
    sortOrder: SortOrder.Asc,
    limit: 1000000,
    offset: 0,
  });
  const existingSerialNumbers = useMemo(() => {
    const serials = new Set<string>();
    (existingAssetsData?.results ?? []).forEach(asset => {
      const sn = asset.serialNumber?.trim();
      if (sn) serials.add(sn);
    });
    return serials;
  }, [existingAssetsData]);
  const statusOptions = useMemo(
    () =>
      Object.keys(assetStatusTranslationKeys)
        // System-managed statuses (e.g. WAREHOUSE_INTAKE) must not be
        // settable via CSV import.
        .filter(
          status => !SYSTEM_MANAGED_ASSET_STATUSES.has(status as AssetStatus)
        )
        .map(status => ({
          value: status,
          label: t(assetStatusTranslationKeys[status as AssetStatus]),
          name: t(assetStatusTranslationKeys[status as AssetStatus]),
        })),
    [t]
  );
  const conditionOptions = useMemo(() => {
    const assetConditionDisplayNameMap = getAssetConditionDisplayNameMap(t);
    return Object.keys(assetConditionDisplayNameMap).map(condition => ({
      value: condition,
      label: assetConditionDisplayNameMap[condition as AssetCondition],
      name: assetConditionDisplayNameMap[condition as AssetCondition],
    }));
  }, [t]);

  const { showSuccessToast, showErrorToast } = useGlobalUI();
  const [fileStatus, setFileStatus] = useState<FileStatus>(
    FileStatus.Undefined
  );
  const [assetList, setAssetList] = useState<CSVImportAsset[]>([]);
  const [fileErrorMessage, setFileErrorMessage] = useState('');
  const [conflictingAssetNames, setConflictingAssetNames] = useState<string[]>(
    []
  );
  const [csvValidationErrors, setCsvValidationErrors] = useState<string[]>([]);
  const [showEnumValues, setShowEnumValues] = useState(false);
  const [showErrorList, setShowErrorList] = useState(false);

  // Returns { language } on success, or { error } string on failure.
  const validateHeader = (
    fileContent: string | ArrayBuffer | null
  ): { language: CSVLanguage } | { error: string } => {
    const parsedData = Papa.parse(fileContent as string, {
      preview: 1,
      header: true,
    });
    const header = (parsedData.meta.fields ?? []) as string[];

    const language = header
      .map((col: string) => assetCSVLanguage(col))
      .find((lang: CSVLanguage | null) => lang !== null);

    if (!language) {
      return { error: t('csvUploadModal.headerError') };
    }

    const csvColumns = assetCSVColumnHeadersMap[language as CSVLanguage];
    const missingColumns = csvColumns.filter(col => !header.includes(col));

    if (missingColumns.length > 0) {
      return {
        error: t('csvUploadModal.missingColumnsError', {
          count: missingColumns.length,
          columns: missingColumns.join(', '),
        }),
      };
    }

    return { language: language as CSVLanguage };
  };

  const discardFileContent = () => {
    setAssetList([]);
    setFileStatus(FileStatus.Undefined);
    setFileErrorMessage('');
    setConflictingAssetNames([]);
    setCsvValidationErrors([]);
  };

  const validateAssets = (
    parsedAssets: CSVImportAsset[],
    rawRows: { [key: string]: string }[],
    csvFormat: Record<string, string>,
    existingSerials: Set<string>
  ): string[] => {
    const errors: string[] = [];
    const seenSerials = new Map<string, number>();

    parsedAssets.forEach((asset, index) => {
      const rowNumber = index + 1; // header is row 0, first data row is row 1
      const rawRow = rawRows[index] ?? {};

      // Validate product type (required lookup)
      const rawProductType = rawRow[csvFormat.productType]?.trim();
      if (!asset.productType) {
        if (rawProductType) {
          errors.push(
            t('csvUploadModal.validationErrors.invalidProductType', {
              row: rowNumber,
              column: csvFormat.productType,
              value: rawProductType,
            })
          );
        } else {
          errors.push(
            t('csvUploadModal.validationErrors.missingProductType', {
              row: rowNumber,
              column: csvFormat.productType,
            })
          );
        }
      }

      // Validate site (optional lookup — error only when a non-empty value doesn't match)
      const rawSite = rawRow[csvFormat.site]?.trim();
      if (rawSite && !asset.site) {
        errors.push(
          t('csvUploadModal.validationErrors.invalidSite', {
            row: rowNumber,
            column: csvFormat.site,
            value: rawSite,
          })
        );
      }

      // Validate assignedTo (optional lookup by email)
      const rawAssignedTo = rawRow[csvFormat.assignedTo]?.trim();
      if (rawAssignedTo && !asset.assignedTo) {
        errors.push(
          t('csvUploadModal.validationErrors.invalidAssignedTo', {
            row: rowNumber,
            column: csvFormat.assignedTo,
            value: rawAssignedTo,
          })
        );
      }

      // Validate status (optional enum)
      const rawStatus = rawRow[csvFormat.status]?.trim();
      if (rawStatus && !asset.status) {
        errors.push(
          t('csvUploadModal.validationErrors.invalidEnumValue', {
            row: rowNumber,
            column: csvFormat.status,
            value: rawStatus,
          })
        );
      }

      // Validate purchase price currency code (optional enum)
      const rawCurrencyCode =
        rawRow[csvFormat.purchasePriceCurrencyCode]?.trim();
      if (rawCurrencyCode && !asset.purchasePriceCurrencyCode) {
        errors.push(
          t('csvUploadModal.validationErrors.invalidEnumValue', {
            row: rowNumber,
            column: csvFormat.purchasePriceCurrencyCode,
            value: rawCurrencyCode,
          })
        );
      }

      // Purchase price and its currency code are required together
      const rawPurchasePrice = rawRow[csvFormat.purchasePrice]?.trim();
      if (rawPurchasePrice && !rawCurrencyCode) {
        errors.push(
          t(
            'csvUploadModal.validationErrors.missingPurchasePriceCurrencyCode',
            {
              row: rowNumber,
              column: csvFormat.purchasePriceCurrencyCode,
            }
          )
        );
      }
      if (rawCurrencyCode && !rawPurchasePrice) {
        errors.push(
          t('csvUploadModal.validationErrors.missingPurchasePrice', {
            row: rowNumber,
            column: csvFormat.purchasePrice,
          })
        );
      }

      // Validate condition (optional enum)
      const rawCondition = rawRow[csvFormat.condition]?.trim();
      if (rawCondition && !asset.condition) {
        errors.push(
          t('csvUploadModal.validationErrors.invalidEnumValue', {
            row: rowNumber,
            column: csvFormat.condition,
            value: rawCondition,
          })
        );
      }

      // Validate serial number for in-file duplicates and system conflicts
      const serial = asset.serialNumber?.trim();
      if (serial) {
        if (seenSerials.has(serial)) {
          errors.push(
            t('csvUploadModal.validationErrors.serialDuplicateInFile', {
              row: rowNumber,
              column: csvFormat.serialNumber,
              value: serial,
              firstRow: seenSerials.get(serial),
            })
          );
        } else {
          seenSerials.set(serial, rowNumber);
          if (existingSerials.has(serial)) {
            errors.push(
              t('csvUploadModal.validationErrors.serialDuplicateInSystem', {
                row: rowNumber,
                column: csvFormat.serialNumber,
                value: serial,
              })
            );
          }
        }
      }

      // Schema validation for format-level fields (name, price, URL, dates)
      try {
        getAssetSchema(t).validateSync(asset, { abortEarly: false });
      } catch (error: unknown) {
        if (error instanceof yup.ValidationError) {
          const innerErrors = error.inner.length > 0 ? error.inner : [error];
          // productType, site, assignedTo, status, condition are validated
          // manually above with richer messages — skip yup duplicates.
          const manuallyValidated = new Set([
            'productType',
            'site',
            'assignedTo',
            'status',
            'condition',
          ]);
          for (const err of innerErrors) {
            if (err.path && manuallyValidated.has(err.path)) continue;
            const columnName = err.path
              ? (csvFormat[err.path] ?? err.path)
              : undefined;
            if (columnName) {
              errors.push(
                t('csvUploadModal.validationErrors.rowColError', {
                  row: rowNumber,
                  column: columnName,
                  message: err.message,
                })
              );
            } else {
              errors.push(
                t('csvUploadModal.validationErrors.rowError', {
                  row: rowNumber,
                  message: err.message,
                })
              );
            }
          }
        }
      }
    });

    return errors;
  };

  const handleSelectedFile = (e: ProgressEvent<FileReader>) => {
    const fileContent = e?.target?.result ?? null;
    setConflictingAssetNames([]);
    setCsvValidationErrors([]);
    setShowErrorList(false);
    setFileErrorMessage('');

    // 1. Validate header columns
    const headerResult = validateHeader(fileContent);
    if ('error' in headerResult) {
      setFileStatus(FileStatus.InvalidHeader);
      setAssetList([]);
      setFileErrorMessage(headerResult.error);
      return;
    }
    const { language } = headerResult;
    const csvFormat = assetCSVLanguageFormat(language);
    if (!csvFormat) {
      setFileStatus(FileStatus.InvalidHeader);
      setAssetList([]);
      setFileErrorMessage(t('csvUploadModal.headerError'));
      return;
    }

    // 2. Parse the full file
    const parsedData = Papa.parse(fileContent as string, {
      header: true,
      skipEmptyLines: true,
    }) as {
      data: { [key: string]: string }[];
      errors: { type: string; row?: number; message: string }[];
    };

    // 3. Report any field-count mismatches before attempting row-level validation
    const fieldMismatchErrors = parsedData.errors
      .filter((e: { type: string }) => e.type === 'FieldMismatch')
      .map((e: { type: string; row?: number; message: string }) => {
        const rowNumber = (e.row ?? 0) + 1; // header is row 0, data rows are 1-indexed
        return t('csvUploadModal.validationErrors.fieldCountError', {
          row: rowNumber,
        });
      });

    if (fieldMismatchErrors.length > 0) {
      setFileStatus(FileStatus.InvalidContent);
      setAssetList([]);
      setCsvValidationErrors(fieldMismatchErrors);
      setFileErrorMessage(
        t('csvUploadModal.documentErrors', {
          count: fieldMismatchErrors.length,
        })
      );
      return;
    }

    // 4. Map raw rows to typed assets
    const rawRows = parsedData.data;
    const parsedAssets: CSVImportAsset[] = rawRows.map(
      (row: { [key: string]: string }) => ({
        name: row[csvFormat.name],
        assetID: row[csvFormat.assetID],
        productType: findOptionCaseInsensitive(
          productTypeOptions.map(opt => ({ ...opt, name: opt.label })),
          row[csvFormat.productType]
        ) as ProductTypeOption | undefined,
        assignedTo: employeeOptions.find(
          option =>
            option.email?.toLowerCase() ===
            row[csvFormat.assignedTo]?.toLowerCase()
        ),
        purchaseDate: row[csvFormat.purchaseDate] || undefined,
        purchasePrice: row[csvFormat.purchasePrice] || undefined,
        purchasePriceCurrencyCode: findCurrencyCodeCaseInsensitive(
          row[csvFormat.purchasePriceCurrencyCode]
        ),
        purchasedFrom: row[csvFormat.purchasedFrom] || undefined,
        brand: row[csvFormat.brand] || undefined,
        model: row[csvFormat.model] || undefined,
        serialNumber: row[csvFormat.serialNumber] || undefined,
        site: findOptionCaseInsensitive(siteOptions, row[csvFormat.site]),
        notes: row[csvFormat.notes] || undefined,
        status: findOptionCaseInsensitive(
          statusOptions,
          row[csvFormat.status]
        ) as AssetStatusOption | undefined,
        condition: findOptionCaseInsensitive(
          conditionOptions,
          row[csvFormat.condition]
        ) as AssetConditionOption | undefined,
        warrantyExpirationDate:
          row[csvFormat.warrantyExpirationDate] || undefined,
      })
    );

    if (parsedAssets.length === 0) {
      setFileStatus(FileStatus.Empty);
      setAssetList([]);
      setFileErrorMessage(t('csvUploadModal.emptyListError'));
      return;
    }

    // 5. Validate all rows
    const validationErrors = validateAssets(
      parsedAssets,
      rawRows,
      csvFormat,
      existingSerialNumbers
    );
    if (validationErrors.length === 0) {
      setFileStatus(FileStatus.Valid);
      setCsvValidationErrors([]);
    } else {
      setFileStatus(FileStatus.InvalidContent);
      setCsvValidationErrors(validationErrors);
      setFileErrorMessage(
        t('csvUploadModal.documentErrors', { count: validationErrors.length })
      );
    }
    setAssetList(parsedAssets);
  };

  const handleSubmit = () => {
    createAssets({
      organizationId: organizationId,
      assets: assetList
        .filter(
          (
            asset
          ): asset is CSVImportAsset & { productType: ProductTypeOption } =>
            asset.productType !== undefined
        )
        .map(asset => ({
          name: asset.name,
          productTypeId: asset.productType.value,
          customAssetId: asset.assetID,
          assignedTo: asset.assignedTo?.value,
          purchaseDate: asset.purchaseDate
            ? new Date(asset.purchaseDate)
            : undefined,
          purchasePrice: asset.purchasePrice
            ? parseFloat(asset.purchasePrice)
            : undefined,
          purchasePriceCurrencyCode: asset.purchasePriceCurrencyCode,
          purchasedFromUrl: asset.purchasedFrom,
          brand: asset.brand,
          model: asset.model,
          serialNumber: asset.serialNumber,
          notes: asset.notes,
          internalNotes: '',
          siteId: asset.site?.value,
          status: asset.status?.value,
          condition: asset.condition?.value,
          imageUrl: undefined,
          warrantyExpirationDate: asset.warrantyExpirationDate
            ? new Date(asset.warrantyExpirationDate)
            : undefined,
        })),
    })
      .unwrap()
      .then(() => {
        showSuccessToast({
          id: 'asset-added-success-toast',
          message: t('csvUploadModal.successMessage'),
        });
        setConflictingAssetNames([]);
        onSuccess();
      })
      .catch(error => {
        console.error('Error while adding assets: ', error);

        if (error?.status === 409) {
          const existingAssets = error?.data?.existing_asset_names || [];
          setConflictingAssetNames(existingAssets);
          return Promise.reject(error);
        }

        // Collect server-side error messages and show them directly to the user
        const serverErrors: string[] = [];

        if (error?.data?.errors && Array.isArray(error.data.errors)) {
          for (const err of error.data.errors) {
            const msg =
              typeof err === 'string'
                ? err
                : (err as { message?: string })?.message;
            if (msg) {
              // Detect serial number duplicate errors and surface a clear explanation
              const isSerialDuplicate =
                /serial/i.test(msg) &&
                /(duplicate|already exist|already in use)/i.test(msg);
              if (isSerialDuplicate) {
                serverErrors.push(
                  t('csvUploadModal.validationErrors.serialDuplicate')
                );
              } else {
                serverErrors.push(msg);
              }
            }
          }
        } else if (error?.data?.message) {
          const msg: string = error.data.message;
          const isSerialDuplicate =
            /serial/i.test(msg) &&
            /(duplicate|already exist|already in use)/i.test(msg);
          serverErrors.push(
            isSerialDuplicate
              ? t('csvUploadModal.validationErrors.serialDuplicate')
              : msg
          );
        }

        if (serverErrors.length > 0) {
          setCsvValidationErrors(serverErrors);
          setFileErrorMessage(
            t('csvUploadModal.documentErrors', { count: serverErrors.length })
          );
          setFileStatus(FileStatus.InvalidContent);
        } else {
          showErrorToast({
            id: 'asset-added-error-toast',
            message: t('csvUploadModal.errorMessage'),
          });
        }

        return Promise.reject(error);
      });
  };

  const footerAction = (
    <ModalAction
      id="csv-modal-add-assets-button"
      type="button"
      disabled={fileStatus !== FileStatus.Valid}
      loading={isCreateAssetsLoading}
      onClick={handleSubmit}>
      {t('csvUploadModal.addAssets')}
    </ModalAction>
  );

  return (
    <ModalV2
      minWidth="600px"
      hide={onCancel}
      visible
      ariaLabelledby="import-assets-modal-header-text">
      <ModalHeader showCloseButton={true}>
        <Flex alignItems="center" columnGap="1rem">
          <Text id="import-assets-modal-header-text" variant="heading-2">
            {t('csvUploadModal.title')}
          </Text>
        </Flex>
      </ModalHeader>
      {isFetchingSites ||
      isFetchingEmployees ||
      isFetchingProductTypes ||
      isFetchingExistingAssets ? (
        <CenteredSpinner />
      ) : isSitesError ||
        isEmployeesError ||
        isProductTypesError ||
        isExistingAssetsError ? (
        <AssetError />
      ) : (
        <form
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}>
          <ModalBody flex="1 1 auto" minHeight={0} overflowY="auto">
            <Text>{t('csvUploadModal.guidelinesTitle')}</Text>
            <ul>
              <li>
                <Text>{t('csvUploadModal.guideline1')}</Text>
              </li>
              <li>
                <Text>
                  {t('csvUploadModal.guideline2', {
                    headers: t('csvUploadModal.columnHeaders'),
                  })}
                </Text>
              </li>
              <li>
                <Text>{t('csvUploadModal.guideline3')}</Text>
              </li>
            </ul>
            <Flex
              alignItems="center"
              justifyContent="center"
              columnGap="1.6rem"
              flexWrap="wrap">
              <Button
                id="button-csv-modal-download-template"
                variant="text"
                iconBefore="download"
                onClick={() => {
                  const language: CSVLanguage = i18n.language.startsWith('fr')
                    ? CSVLanguages.FR
                    : i18n.language.startsWith('es')
                      ? CSVLanguages.ES
                      : CSVLanguages.EN;
                  const format = assetCSVLanguageFormat(language);
                  if (format) {
                    const statusMap: Record<string, string> = {};
                    statusOptions.forEach(opt => {
                      statusMap[opt.value] = opt.label;
                    });
                    const conditionMap: Record<string, string> = {};
                    conditionOptions.forEach(opt => {
                      conditionMap[opt.value] = opt.label;
                    });
                    exportCSV(
                      generateImportTemplateCSVData(format, {
                        status: statusMap,
                        condition: conditionMap,
                      }),
                      'assetsTemplate.csv'
                    );
                  }
                }}>
                {t('csvUploadModal.downloadTemplate')}
              </Button>
              <Button
                id="button-csv-modal-show-enum-values"
                variant="text"
                onClick={() => setShowEnumValues(v => !v)}>
                {showEnumValues
                  ? t('csvUploadModal.hideEnumValues')
                  : t('csvUploadModal.showEnumValues')}
              </Button>
            </Flex>
            {showEnumValues && (
              <Flex
                mt={space_xs}
                width="100%"
                p={spaces.space_s}
                columnGap={spaces.space_m}
                overflow="auto"
                backgroundColor={enumPanelBackgroundColor}
                border={`1px solid ${enumPanelBorderColor}`}
                borderRadius={sizes.size_4}>
                <EnumValuesList
                  title={t('csvUploadModal.enumValuesTable.assetTypes')}
                  values={productTypeOptions}
                />
                <EnumValuesList
                  title={t('csvUploadModal.enumValuesTable.statuses')}
                  values={statusOptions}
                />
                <EnumValuesList
                  title={t('csvUploadModal.enumValuesTable.conditions')}
                  values={conditionOptions}
                />
                <EnumValuesList
                  title={t('csvUploadModal.enumValuesTable.sites')}
                  values={siteOptions}
                />
                <EnumValuesList
                  title={t('csvUploadModal.enumValuesTable.currencyCodes')}
                  values={Object.values(CurrencyCodeEnum).map(code => ({
                    value: code,
                    label: code,
                  }))}
                />
              </Flex>
            )}
            {conflictingAssetNames.length > 0 && (
              <Box mb={space_xs}>
                <Banner
                  id="asset-conflict-banner"
                  intent="error"
                  title={t('csvUploadModal.conflictBanner.title')}
                  isFlat
                  isFullWidth
                  message={() => (
                    <Flex flexDirection="column" rowGap="0.8rem">
                      <Text variant="body-2">
                        {t('csvUploadModal.conflictBanner.messageStart', {
                          count: conflictingAssetNames.length,
                        })}{' '}
                        {conflictingAssetNames.length === 1
                          ? t('csvUploadModal.conflictBanner.itExists')
                          : t('csvUploadModal.conflictBanner.theyExist')}{' '}
                        {t('csvUploadModal.conflictBanner.inYourOrganization')}{' '}
                        <b>{t('csvUploadModal.conflictBanner.restAdded')}</b>
                      </Text>
                      <InlineAccordion
                        label={t(
                          'csvUploadModal.conflictBanner.viewConflicts',
                          {
                            count: conflictingAssetNames.length,
                          }
                        )}>
                        <ul>
                          {conflictingAssetNames.map((name, index) => (
                            <li key={index}>
                              <Text variant="body-2">{name}</Text>
                            </li>
                          ))}
                        </ul>
                      </InlineAccordion>
                    </Flex>
                  )}
                />
              </Box>
            )}
            <Box mt={space_xs}>
              <FilePicker
                onLoad={handleSelectedFile}
                onRemove={discardFileContent}
                acceptedExtensions=".csv"
                errorMessage={fileErrorMessage}
                dragAndDropText={t('csvUploadModal.filePicker.dragAndDropText')}
                uploadButtonText={t(
                  'csvUploadModal.filePicker.uploadButtonText'
                )}
                changeFileButtonText={t(
                  'csvUploadModal.filePicker.changeFileButtonText'
                )}
                cancelButtonText={t(
                  'csvUploadModal.filePicker.cancelButtonText'
                )}
                fileSizeExceedsMaxErrorText={t(
                  'csvUploadModal.filePicker.fileSizeExceedsMaxErrorText'
                )}
                fileReadErrorText={t(
                  'csvUploadModal.filePicker.fileReadErrorText'
                )}
                invalidFileTypeErrorText={t(
                  'csvUploadModal.filePicker.invalidFileTypeErrorText'
                )}
                formatAcceptedFileTypesText={extensions =>
                  t('csvUploadModal.filePicker.formatAcceptedFileTypesText', {
                    extensions,
                  })
                }
              />
            </Box>
            {csvValidationErrors.length > 0 && (
              <Box mt={space_xs}>
                <Banner
                  id="asset-validation-errors-banner"
                  intent="error"
                  title={t('csvUploadModal.validationErrors.bannerTitle')}
                  isFlat
                  isFullWidth
                  message={() => (
                    <Flex flexDirection="column">
                      <Flex
                        alignItems="center"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setShowErrorList(v => !v)}>
                        <Icon
                          icon={showErrorList ? 'chevron-up' : 'chevron-down'}
                          size="xsmall"
                        />
                        <Text variant="body-2" ml="0.4rem">
                          {t('csvUploadModal.validationErrors.viewAllErrors', {
                            count: csvValidationErrors.length,
                          })}
                        </Text>
                      </Flex>
                      {showErrorList && (
                        <ul
                          style={{
                            paddingLeft: '1.2rem',
                            margin: '0.4rem 0 0',
                          }}>
                          {csvValidationErrors.map((error, index) => (
                            <li key={index}>
                              <Text variant="body-2">{error}</Text>
                            </li>
                          ))}
                        </ul>
                      )}
                    </Flex>
                  )}
                />
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <ModalFooterActions>
              <ModalAction id="asset-upload-csv-cancel-button" variant="text">
                {t('csvUploadModal.cancel')}
              </ModalAction>
              {footerAction}
            </ModalFooterActions>
          </ModalFooter>
        </form>
      )}
    </ModalV2>
  );
};

export default AssetsCSVUploadModal;
